import { error } from '@pressreader/logger';

import { isIdentityScoped } from '../identity.scope';

import type { IdentityEndpointDefinition, IdentityPolicy, IdentityPolicyApi } from './contract';
import { apiAccess } from './rtk.access';

/** Per-api, per-ENDPOINT policy registry. Keyed by api instance so a spec's api can never see another's. */
export const registry = new Map<IdentityPolicyApi, Map<string, IdentityPolicy>>();

const ENDPOINT_TYPE_QUERY = 'query';
const ENDPOINT_TYPE_INFINITE_QUERY = 'infinitequery';

/**
 * Query endpoints on the api that no policy covers — the gap both gates report. Mutations are out of scope for
 * the identity policy: their cached results are consumer-owned, and no public API can remove one. Only queries
 * hold identity-scoped cache entries.
 */
function undeclaredQueryEndpoints(api: IdentityPolicyApi, policies: Map<string, IdentityPolicy>): string[] {
    const undeclared = Object.keys(api.endpoints).filter(endpointName => !policies.has(endpointName));
    if (undeclared.length === 0) {
        return [];
    }
    const definitions = readEndpointDefinitions(api, undeclared);

    return undeclared.filter(endpointName => isQueryEndpoint(definitions[endpointName]));
}

/**
 * The ONE reporter for the coverage gap, used by both gates, so a forgotten endpoint reads identically wherever it
 * is caught. Returns the gap for a best-effort repair: nothing in dev/test (it throws first), the endpoints
 * themselves in production.
 *
 * The two gates differ only in consequence. From `declareIdentityPolicy` it runs at IMPORT, so the throw fails the
 * feature's own spec run — this is where a gap becomes unshippable. From the commit-time sweep it is the RUNTIME
 * backstop: a throw there surfaces inside a listener effect, which the listener middleware isolates and routes to
 * the logger, so the store and sibling listeners carry on and no build fails on it.
 *
 * Now that the repair is a direct per-key `initiate`, the production path covers an UNTAGGED forgotten endpoint
 * too — the previous tag-based backstop could do nothing at all for one, because an endpoint that provides no tags
 * cannot be reached by any invalidation.
 */
export function reportUndeclaredQueryEndpoints(api: IdentityPolicyApi, policies: Map<string, IdentityPolicy>): string[] {
    const undeclaredQueries = undeclaredQueryEndpoints(api, policies);
    if (undeclaredQueries.length === 0) {
        return [];
    }
    reportProblems(
        "Query endpoint(s) with no identity policy — user-scoped data would survive an identity change. A feature must declare a policy for EVERY query endpoint it injects, at the injection site: declareIdentityPolicy(api, { <endpoint>: { kind: 'purge' | 'revalidate' | 'invariant' } }). Missing:",
        undeclaredQueries,
    );
    return undeclaredQueries;
}

export function validate(endpointName: string, policy: IdentityPolicy, definition: IdentityEndpointDefinition | undefined): string[] {
    if (!definition) {
        return [`${endpointName}: no such endpoint on this api`];
    }
    if (!isQueryEndpoint(definition)) {
        return [`${endpointName}: not a query endpoint — identity policies apply to queries only`];
    }
    if (policy.kind === 'invariant') {
        return [];
    }
    // `providesTags` is deliberately NOT required: the reconcile repairs each cached entry with its own
    // `initiate`, so nothing here consumes tags.
    return isIdentityScoped(definition.queryFn)
        ? []
        : [`${endpointName}: queryFn is not wrapped in identityScoped(...) — a '${policy.kind}' endpoint must be fenced`];
}

/**
 * Read endpoint DEFINITIONS (queryFn, type) through `enhanceEndpoints`, the only public path
 * to them — `api.endpoints[name]` exposes the generated `initiate`/`select` bridge, not the definition.
 * Passing a callback makes it a pure read: `enhanceEndpoints` only mutates what the callback mutates.
 */
export function readEndpointDefinitions(api: IdentityPolicyApi, endpointNames: string[]): Record<string, IdentityEndpointDefinition | undefined> {
    const definitions: Record<string, IdentityEndpointDefinition | undefined> = {};
    const readers: Record<string, (definition: IdentityEndpointDefinition | undefined) => void> = {};
    for (const endpointName of endpointNames) {
        readers[endpointName] = definition => {
            definitions[endpointName] = definition;
        };
    }
    apiAccess(api).enhanceEndpoints({ endpoints: readers });
    return definitions;
}

function isQueryEndpoint(definition: IdentityEndpointDefinition | undefined): boolean {
    return definition?.type === ENDPOINT_TYPE_QUERY || definition?.type === ENDPOINT_TYPE_INFINITE_QUERY;
}

export function getOrCreateApiEntry(api: IdentityPolicyApi): Map<string, IdentityPolicy> {
    const existing = registry.get(api);
    if (existing) {
        return existing;
    }
    const created = new Map<string, IdentityPolicy>();
    registry.set(api, created);
    return created;
}

/** Unshippable in dev/test (throws, so CI catches it), loud but non-fatal in production. */
export function reportProblems(headline: string, problems: string[]): void {
    if (problems.length === 0) {
        return;
    }
    const failure = new Error(`${headline}\n- ${problems.join('\n- ')}`);
    if (process.env['NODE_ENV'] === 'production') {
        error(failure);
        return;
    }
    throw failure;
}
